import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Speed } from "@mui/icons-material";
import {
  getWorkflow,
  patchWorkflow,
  WorkflowDetail,
} from "../../api/workflows";
import { MeResponse } from "../../api/me";
import { ApiError, extractErrorMessage } from "../../api/client";

// Workflow kinds whose orchestrator stage-fills against a concurrency
// cap. Anything not in this set ignores the field, so we hide the
// widget rather than offering a setting that does nothing.
const CONCURRENCY_AWARE_KINDS = new Set([
  "batch_export",
  "batch_onboard",
  "batch_rebuild",
]);

const TERMINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);

function readConcurrency(wf: WorkflowDetail): number | null {
  try {
    const params = JSON.parse(wf.params_json) as { concurrency?: number };
    return typeof params.concurrency === "number" ? params.concurrency : null;
  } catch {
    return null;
  }
}

// Admin-only inline editor for the workflow's concurrency cap. The
// orchestrator slot-fills jobs against this number on each tick;
// bumping it lets more jobs run in parallel from the next tick
// onward (already-running jobs aren't preempted).
//
// Self-gates: renders nothing if the caller isn't an admin, the
// workflow's kind doesn't honor the field, or the workflow is
// already in a terminal state.
//
// On save, calls patchWorkflow + then getWorkflow (PATCH responses
// omit the child Jobs; we need the full GET shape so the per-job
// table downstream doesn't crash). `onSaved` lets the parent fold
// the fresh workflow into its own state without us reaching up.
interface Props {
  wf: WorkflowDetail;
  me: MeResponse | null;
  onSaved: (fresh: WorkflowDetail) => void;
}

export function ConcurrencyEditor({ wf, me, onSaved }: Props) {
  const [draft, setDraft] = useState<string>("");
  // True once the user has typed in the input. While false, the
  // mirror effect keeps draft in sync with the workflow's current
  // value as polled updates land. Setting it true freezes draft
  // until a save (which untouches it) or unmount.
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isTerminal = TERMINAL_STATUSES.has(wf.status);
  const show =
    me?.role === "admin" && !isTerminal && CONCURRENCY_AWARE_KINDS.has(wf.kind);

  // Mirror the workflow's current concurrency into the input whenever
  // the user hasn't typed anything (so spinner +/- start from a real
  // value, not zero). When current is null (operator auto-default),
  // we leave the field blank and let the placeholder hint instead.
  useEffect(() => {
    if (touched) return;
    const cur = readConcurrency(wf);
    setDraft(cur === null ? "" : String(cur));
  }, [wf, touched]);

  if (!show) return null;

  const current = readConcurrency(wf);
  const draftN = parseInt(draft, 10);
  const draftValid = Number.isFinite(draftN) && draftN >= 1 && draftN <= 64;
  // Save only fires when the input parses to a valid number AND
  // that number differs from the workflow's current setting —
  // otherwise we'd be patching no-ops on every click.
  const dirty = draftValid && draftN !== current;

  const handleSave = async () => {
    if (!draftValid) {
      setErr("concurrency must be an integer between 1 and 64");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await patchWorkflow(wf.id, { concurrency: draftN });
      const fresh = await getWorkflow(wf.id);
      onSaved(fresh);
      // Untouch so the mirror effect repopulates the draft from the
      // freshly-saved current value. Save will then re-disable.
      setTouched(false);
    } catch (e) {
      setErr(
        extractErrorMessage(
          e,
          e instanceof ApiError ? `patch returned ${e.status}` : undefined,
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <Speed color="primary" fontSize="small" />
        <Typography variant="overline">Concurrency</Typography>
      </Stack>
      <Typography
        variant="caption"
        component="div"
        color="text.secondary"
        sx={{ mb: 1.5 }}
      >
        {current === null
          ? "Currently using the operator's auto-default (≤4 in flight)."
          : `Currently set to ${current}.`}{" "}
        Bumping it up lets the orchestrator start more jobs in parallel on
        its next tick — already-running jobs aren't preempted.
      </Typography>
      <Stack direction="row" alignItems="center" gap={2}>
        <TextField
          size="small"
          type="number"
          label="Concurrency"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setTouched(true);
          }}
          placeholder={current === null ? "auto" : undefined}
          inputProps={{ min: 1, max: 64, step: 1 }}
          disabled={saving}
          sx={{ width: 140 }}
        />
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Stack>
      {err && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {err}
        </Alert>
      )}
    </Paper>
  );
}
